import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import moment from "moment/moment";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { useBookingContext } from "../../context/BookingContext";
import { selfDriveConditions } from "../../utils/selfDriveConditions";
import { createDurations, getDefaultDates } from "../../utils";
import wirasanaJson from "../../utils/wirasana-map.json";

// Import komponen yang sudah dipisah
import BookingHero from "./BookingHero";
import BookingForm from "./BookingForm";
import DriverConditionSelector from "./DriverConditionSelector";
import BookingTypeSelector from "./BookingTypeSelector";
import TourPackageSelector from "./TourPackageSelector";
import LocationDateSection from "./LocationDateSection";

function BookCarSection() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data, setData } = useBookingContext();

  const [type, setType] = useState("");
  const [tourPackage, setTourPackage] = useState("6");
  const [duration, setDuration] = useState(1);
  const [selfDriveCondition, setSelfDriveCondition] = useState("without_driver");

  // Date states
  const { defaultPickupDate, defaultDropOffDate } = getDefaultDates(data);
  const [datePickup, setDatePickup] = useState(defaultPickupDate);
  const [dateDropOff, setDateDropOff] = useState(defaultDropOffDate);

  // Helper functions
  const getValidationSchema = (currentType, translation) => {
    const schemas = {
      self_drive: Yup.object().shape({
        type: Yup.string().required("Type is required"),
        pickup_date: Yup.date()
          .when("pickup_location", {
            is: (prev) => prev !== "Wirasana Rent A Car",
            then: () =>
              Yup.date().min(new Date(new Date().getTime() + 24 * 60 * 60 * 1000), translation("reservation_24")),
          })
          .typeError("Must be a valid date")
          .required("Pickup date is required"),
        dropoff_date: Yup.date()
          .when("pickup_date", (pickup_date, schema) => {
            return schema.test({
              test: function (dropoff_date) {
                const start = new Date(pickup_date);
                const end = new Date(dropoff_date);
                return end > start;
              },
              message: translation("drop_after_pickup"),
            });
          })
          .required("Drop-off date is required"),
        pickup_location: Yup.string().nullable(),
        dropoff_location: Yup.string().nullable(),
      }),
      tour: Yup.object().shape({
        type: Yup.string().required("Type is required"),
        pickup_date: Yup.date()
          .typeError("Must be a valid date")
          .min(new Date(new Date().getTime() + 12 * 60 * 60 * 1000), translation("reservation_12"))
          .required("Pickup date is required"),
        pickup_location: Yup.string().nullable(),
        dropoff_location: Yup.string().nullable(),
      }),
      default: Yup.object().shape({
        type: Yup.string().required("Type is required"),
        pickup_date: Yup.date()
          .typeError("Must be a valid date")
          .when("pickup_location", {
            is: (prev) => prev !== "Wirasana Rent A Car",
            then: () =>
              Yup.date().min(new Date(new Date().getTime() + 24 * 60 * 60 * 1000), translation("reservation_24")),
          })
          .required("Pickup date is required"),
        pickup_location: Yup.string().nullable(),
        dropoff_location: Yup.string().nullable(),
      }),
    };

    return schemas[currentType] || schemas.default;
  };

  const getInitialValues = (contextData) => ({
    type: "",
    tourPackage: "6",
    pickup_date: "",
    dropoff_date: "",
    duration: 1,
    conditions: { with_driver: false },
    pickup_location: contextData?.pickup_location || null,
    dropoff_location: contextData?.dropoff_location || null,
    pickup_location_json: contextData?.pickup_location_json || null,
    dropoff_location_json: contextData?.dropoff_location_json || null,
  });

  // Handler functions
  const handleFormSubmit = (values, { setValues }) => {
    const finalValues = prepareFormData(values);
    setValues(finalValues);
    setData({ ...finalValues, driver: selfDriveCondition });
    navigate("select-vehicle");
  };

  // Formik configuration
  const formik = useFormik({
    initialValues: getInitialValues(data),
    validationSchema: getValidationSchema(type, t),
    onSubmit: handleFormSubmit,
  });

  // Effects
  useEffect(() => {
    updateFormDates();
  }, [datePickup, dateDropOff, tourPackage, type, duration]);

  useEffect(() => {
    initializeFormData();
  }, []);

  const handleSelectType = (selectedType) => {
    setType(selectedType);
    formik.setFieldValue("type", selectedType);
  };

  const handleSelectPackage = (selectedPackage) => {
    setTourPackage(selectedPackage);
    formik.setFieldValue("tourPackage", selectedPackage);
  };

  const handleSelectDuration = (selectedDuration) => {
    setDuration(selectedDuration);
    formik.setFieldValue("duration", selectedDuration);
  };

  const handleDriverConditionChange = (condition) => {
    setType("");
    formik.setFieldValue("type", "");
    setSelfDriveCondition(condition);
    formik.setFieldValue("conditions", {
      with_driver: condition === "with_driver",
    });
  };

  const updateFormDates = () => {
    formik.setFieldValue("pickup_date", moment(datePickup).format("YYYY/MM/DD HH:mm"));

    if (type === "tour") {
      formik.setFieldValue("dropoff_date", moment(datePickup).add(duration, "days").format("YYYY/MM/DD HH:mm"));
    } else {
      formik.setFieldValue("dropoff_date", moment(dateDropOff).format("YYYY/MM/DD HH:mm"));
    }
  };

  const initializeFormData = () => {
    if (data?.type) {
      setType(data.type);
      formik.setFieldValue("type", data.type);
    }
    if (data?.driver) {
      setSelfDriveCondition(data.driver);
    }
    if (data?.type === "tour") {
      setTourPackage(data.tourPackage || "6");
      setDuration(data.duration || 1);
      formik.setFieldValue("tourPackage", data.tourPackage);
      formik.setFieldValue("duration", data.duration);
    }
  };

  const prepareFormData = (values) => ({
    ...values,
    pickup_location: values.pickup_location || "Wirasana Rent A Car",
    dropoff_location: values.dropoff_location || "Wirasana Rent A Car",
    pickup_location_json: values.pickup_location_json || JSON.stringify(wirasanaJson),
    dropoff_location_json: values.dropoff_location_json || JSON.stringify(wirasanaJson),
  });

  return (
    <div id="booking-section" className="relative flex items-center justify-center min-h-screen p-4 md:p-8">
      <BookingHero />

      <div className="w-full max-w-[133rem] grid grid-cols-1 lg:grid-cols-2 gap-8 items-center relative z-10">
        <BookingHero.Content />

        <BookingForm
          title={t("book_a_car") || "Book Your Car Today!"}
          onSubmit={formik.handleSubmit}
          submitText={t("book_now")?.toUpperCase() || "BOOK NOW"}
        >
          <DriverConditionSelector
            conditions={selfDriveConditions}
            selectedCondition={selfDriveCondition}
            onConditionChange={handleDriverConditionChange}
            t={t}
          />

          <BookingTypeSelector
            type={type}
            onTypeChange={handleSelectType}
            selfDriveCondition={selfDriveCondition}
            t={t}
            error={formik.errors.type}
            touched={formik.touched.type}
          />

          {type === "tour" && (
            <TourPackageSelector
              tourPackage={tourPackage}
              duration={duration}
              onPackageChange={handleSelectPackage}
              onDurationChange={handleSelectDuration}
              durations={createDurations()}
              t={t}
            />
          )}

          <LocationDateSection
            type={type}
            formik={formik}
            datePickup={datePickup}
            dateDropOff={dateDropOff}
            onDatePickupChange={setDatePickup}
            onDateDropOffChange={setDateDropOff}
            t={t}
          />
        </BookingForm>
      </div>
    </div>
  );
}

export default BookCarSection;
